import { notFoundPage } from "@/utils/notFound";
import { GetServerSideProps } from "next";
import CategoryHub from "@/components/hub/CategoryHub";
import type { HubCategory } from "@/utils/categoryHubs";

interface Props {
  category: HubCategory;
  related: HubCategory[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  voices: any[];
  page: number;
  lastPage: number;
}

export default function CategoryVoicesPage(props: Props) {
  return (
    <CategoryHub
      // Fresh sort/search state per page: a client-side hop between pages
      // reuses this component otherwise.
      key={`${props.category.slug}:${props.page}`}
      category={props.category}
      related={props.related}
      voices={props.voices}
      page={props.page}
      lastPage={props.lastPage}
    />
  );
}

// Page 1 is the trending grid. ?page=N (N >= 2) lists the whole category in a
// stable order (see fetchCategoryPage) so every voice has a crawlable link.
export const getServerSideProps: GetServerSideProps = async ({
  params,
  query,
  res,
}) => {
  const slug = String(params?.category || "");
  let page = 1;
  if (query.page !== undefined) {
    if (typeof query.page !== "string" || !/^[1-9][0-9]{0,5}$/.test(query.page))
      return notFoundPage({ res });
    page = Number(query.page);
    if (page === 1) {
      return {
        redirect: { destination: `/ai-voices/${slug}`, permanent: true },
      };
    }
  }
  try {
    const {
      resolveCategorySlug,
      getHubCategories,
      fetchCategoryVoices,
      fetchCategoryPage,
      getCategoryVoiceIds,
      hubLastPage,
      HUB_PAGE_SIZE,
      MAX_HUB_PAGE,
    } = await import("@/utils/categoryHubs");

    const category = await resolveCategorySlug(slug);
    if (!category) return notFoundPage({ res });
    if (page > MAX_HUB_PAGE) return notFoundPage({ res });

    const [listing, all] = await Promise.all([
      page === 1
        ? Promise.all([
            fetchCategoryVoices(category.badge, HUB_PAGE_SIZE),
            // Only sizes the page links; page 1 renders without them.
            getCategoryVoiceIds(category.badge).catch((error) => {
              console.error("hub voice ids failed:", slug, error);
              return [] as string[];
            }),
          ]).then(([voices, ids]) => ({
            voices,
            lastPage: hubLastPage(ids.length),
          }))
        : fetchCategoryPage(category.badge, page),
      getHubCategories(50),
    ]);
    if (page > listing.lastPage || (page > 1 && listing.voices.length === 0))
      return notFoundPage({ res });
    const related = all.filter((c) => c.slug !== category.slug).slice(0, 12);

    res.setHeader(
      "Cache-Control",
      "public, s-maxage=600, stale-while-revalidate=1200",
    );

    return {
      props: {
        category,
        related,
        voices: listing.voices,
        page,
        lastPage: listing.lastPage,
      },
    };
  } catch (error) {
    console.error("Category hub SSR error:", slug, error);
    // A failed lookup is a 500, not "this hub does not exist".
    res.setHeader("Cache-Control", "private, no-store");
    throw error;
  }
};
